// src/pages/subscription/SubscriptionUsagePage.tsx

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useSubscription, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
  Badge,
  ErrorAlert,
} from '@react/components';

function formatLabel(value: string, prefix: string): string {
  return value.replace(prefix, '').split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '--';
  try { return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); } catch { return String(dateStr); }
}

function getProgressColor(percentage: number): string {
  if (percentage > 90) return 'bg-destructive';
  if (percentage >= 70) return 'bg-yellow-500';
  return 'bg-primary';
}

export const SubscriptionUsagePage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = useSubscription();
  const { t } = useI18n();

  const [features, setFeatures] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showRecordForm, setShowRecordForm] = useState(false);
  const [recordForm, setRecordForm] = useState({ feature_id: '', quantity: '', source: '' });
  const [recordSubmitting, setRecordSubmitting] = useState(false);

  const fetchUsage = () => {
    if (!id) return;
    setLoading(true);
    store.GetFeatureUsage(id).then((res: any) => {
      const list = res?.features ?? res?.items ?? (res?.usage ? [res.usage] : []);
      setFeatures(Array.isArray(list) ? list : []);
    }).catch(() => {
      // Error handled by store
    }).finally(() => {
      setLoading(false);
    });
  };

  useEffect(() => {
    fetchUsage();
  }, [id]);

  const handleRecordUsage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !recordForm.feature_id || !recordForm.quantity) return;
    setRecordSubmitting(true);
    try {
      // RecordUsage was renamed to CreateFeatureUsageRecord in the regenerated API.
      await store.CreateFeatureUsageRecord(id, {
        feature_id: recordForm.feature_id,
        quantity: Number(recordForm.quantity),
        source: recordForm.source || undefined,
      });
      setShowRecordForm(false);
      setRecordForm({ feature_id: '', quantity: '', source: '' });
      fetchUsage();
    } catch {
      // Error handled by store
    } finally {
      setRecordSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <nav className="text-sm mb-2">
            <Link to="/subscriptions" className="text-muted-foreground hover:text-foreground">
              {t('subscription.list')}
            </Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <Link to={`/subscriptions/${id}`} className="text-muted-foreground hover:text-foreground">
              {id}
            </Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <span className="text-foreground">{t('subscription.usage')}</span>
          </nav>
          <h1 className="text-2xl font-bold text-foreground">{t('subscription.usage')}</h1>
        </div>
        <Button onClick={() => setShowRecordForm(!showRecordForm)}>
          {showRecordForm ? t('common.cancel') : t('subscription.recordUsage')}
        </Button>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Record Usage Form */}
      {showRecordForm && (
        <Card>
          <CardHeader>
            <CardTitle>{t('subscription.recordUsage')}</CardTitle>
          </CardHeader>
          <form onSubmit={handleRecordUsage}>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    {t('subscription.featureId')}
                  </label>
                  <Input
                    placeholder={t('subscription.featureIdPlaceholder')}
                    value={recordForm.feature_id}
                    onChange={(e) => setRecordForm({ ...recordForm, feature_id: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    {t('subscription.quantity')}
                  </label>
                  <Input
                    type="number"
                    placeholder="1"
                    value={recordForm.quantity}
                    onChange={(e) => setRecordForm({ ...recordForm, quantity: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    {t('subscription.source')}
                  </label>
                  <Input
                    placeholder={t('subscription.sourcePlaceholder')}
                    value={recordForm.source}
                    onChange={(e) => setRecordForm({ ...recordForm, source: e.target.value })}
                  />
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex justify-end gap-2">
              <Button variant="secondary" type="button" onClick={() => setShowRecordForm(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={recordSubmitting}>
                {recordSubmitting ? t('common.saving') : t('subscription.recordUsage')}
              </Button>
            </CardFooter>
          </form>
        </Card>
      )}

      {/* Usage Features */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">{t('common.loading')}</div>
        </div>
      ) : features.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-muted-foreground">{t('subscription.noUsageData')}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map((feature: any, idx: number) => {
            const featureId = feature.entity?.id || feature.id || feature.feature_id || idx;
            const usagePercentage = feature.usage_percentage ?? (
              feature.limit_value > 0
                ? Math.min(100, Math.round(((feature.current_usage ?? 0) / feature.limit_value) * 100))
                : 0
            );
            const progressColor = getProgressColor(usagePercentage);

            return (
              <Card key={featureId}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-base">
                      {String(feature.name ?? feature.feature_name ?? '--')}
                    </CardTitle>
                    {feature.type && (
                      <Badge variant="secondary" size="sm">
                        {formatLabel(feature.type, 'FEATURE_TYPE_')}
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {/* Usage numbers */}
                    <div className="flex items-baseline justify-between">
                      <span className="text-2xl font-bold text-foreground">
                        {String(feature.current_usage ?? 0)}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        / {feature.limit_value != null ? String(feature.limit_value) : t('subscription.unlimited')}
                      </span>
                    </div>

                    {/* Progress bar */}
                    {feature.limit_value != null && feature.limit_value > 0 && (
                      <div className="space-y-1">
                        <div className="bg-muted rounded-full h-2">
                          <div
                            className={`${progressColor} rounded-full h-2 transition-all`}
                            style={{ width: `${Math.min(100, usagePercentage)}%` }}
                          />
                        </div>
                        <p className="text-xs text-muted-foreground text-right">
                          {usagePercentage}%
                        </p>
                      </div>
                    )}

                    {/* Period info */}
                    {(feature.period_start || feature.period_end) && (
                      <p className="text-xs text-muted-foreground">
                        {formatDate(feature.period_start)} - {formatDate(feature.period_end)}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
});

export default SubscriptionUsagePage;
